import { useEffect } from 'react';

import { setSessionStorageItem, getSessionStorageItem } from '@revenuemarket/browser';
import { ResponseStatus } from '@revenuemarket/http';
import { Steps, StepModel as StepModelType } from '@revenuemarket/react';
import { Toast } from '@revenuemarket/ui';
import { getCookie } from 'cookies-next';
import { AnimatePresence } from 'framer-motion';
import { useRouter } from 'next/router';
import { match } from 'ts-pattern';

import SignupPasswordStep from '~components/Auth/signup/steps/SignupPasswordStep';
import { useSignUp } from '~hooks/queries/auth/query';
import { css } from '~libs/emotion';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { mixpanel } from '~pages/_app';
import { StepModel } from '~pages/auth/signup';
import { getOauthSignupContext } from '~utils/auth/oauthSession';
import { trackNaverConversion } from '~utils/naver-analytics';
import { getCompanyInvitationSession } from '~utils/sesion/companyInvitationSession';

import SignupEmailStep from './SignupEmailStep';
import SignupEmailVerificationStep from './SignupEmailVertificationStep';
import SignupInfoStep, { SignupInfoStepModel } from './SignupInfoStep';

function toCamelCase(str: string) {
  return str.replace(/_([a-z])/g, function (match, letter) {
    return letter.toUpperCase();
  });
}

const REFERRER_USER_ID_KEY = 'referer_user_id' as const;

const utmParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

function generateRandomPassword(length = 12) {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  const specials = '!@#$%^&*()_+-=';
  const charset = `${letters}${digits}${specials}`;
  const array = new Uint32Array(length);
  window.crypto.getRandomValues(array);

  const password = Array.from(array, (x) => charset[x % charset.length]).join('');
  const fallbackType = Math.random() < 0.5 ? digits : specials;
  const requiredChar = fallbackType[Math.floor(Math.random() * fallbackType.length)];

  return `${requiredChar}${password.slice(1)}`;
}

const SignupSteps = ({
  stepModel,
  moveStep,
  isPartnerIntro,
  host,
}: {
  stepModel: StepModelType<StepModel>;
  moveStep: (value: StepModelType<StepModel>) => void;
  isPartnerIntro: boolean;
  host: string;
}) => {
  const router = useRouter();
  const refererUserId = router.query.referrerId;

  const oauthSignupContext = getOauthSignupContext();
  const isOnOAuthFlow = oauthSignupContext !== undefined;

  useEffect(() => {
    const oauthEmail = oauthSignupContext?.email;

    if (oauthEmail) {
      moveStep({
        step: 3,
        model: {
          email: oauthEmail,
          passcode: '',
          password: generateRandomPassword(12),
          name: oauthSignupContext?.name,
          phoneNumber: oauthSignupContext?.phone,
        } as SignupInfoStepModel,
      });
    }
  }, [oauthSignupContext?.email]);

  const { mutateAsync: signUp } = useSignUp();

  useEffect(() => {
    if (refererUserId) {
      setSessionStorageItem(REFERRER_USER_ID_KEY, refererUserId);
    }
  }, [refererUserId]);

  return (
    <AnimatePresence>
      <Steps
        stepModel={stepModel}
        className={
          stepModel.step === 0
            ? css`
                width: 100% !important;
              `
            : ''
        }
      >
        <Steps.Item<StepModel, 0> step={0}>
          {(model) => (
            <SignupEmailStep
              model={{
                ...model,
                isOnOAuthFlow,
              }}
              onFinish={(model) => moveStep({ step: 1, model })}
              isPartnerIntro={isPartnerIntro}
              host={host}
            />
          )}
        </Steps.Item>
        <Steps.Item<StepModel, 1> step={1}>
          {(model) => (
            <SignupEmailVerificationStep
              model={model}
              onBack={() =>
                moveStep({
                  step: 0,
                  model: {
                    ...model,
                    isOnOAuthFlow,
                  },
                })
              }
              onFinish={(model) => {
                const nextModel: SignupInfoStepModel = {
                  ...model,
                  name: oauthSignupContext?.name,
                  phoneNumber: oauthSignupContext?.phone,
                  // OAuth 플로우일 때만 랜덤 패스워드 생성
                  ...(isOnOAuthFlow && {
                    password: generateRandomPassword(12),
                  }),
                };

                moveStep({
                  step: 2,
                  model: nextModel,
                });
              }}
            />
          )}
        </Steps.Item>
        <Steps.Item<StepModel, 2> step={2}>
          {(model) => (
            <SignupPasswordStep
              model={model}
              onFinish={(values) => {
                const nextModel: SignupInfoStepModel = {
                  ...model,
                  ...values,
                  name: oauthSignupContext?.name,
                  phoneNumber: oauthSignupContext?.phone,
                };
                moveStep({ step: 3, model: nextModel });
              }}
            />
          )}
        </Steps.Item>
        <Steps.Item<StepModel, 3> step={3}>
          {(model) => {
            return (
              <SignupInfoStep
                model={model}
                onFinish={async (values) => {
                  const referrerClobeUserId = getSessionStorageItem<string>(REFERRER_USER_ID_KEY);
                  const partner = getSessionStorageItem<string>('partner');
                  const parsedUtmParams = utmParams.reduce(
                    (acc: { [key: string]: string }, key) => {
                      const value = getCookie(key) as string;
                      if (value) {
                        acc[toCamelCase(key)] = value;
                      }
                      return acc;
                    },
                    {},
                  );

                  const marketingParams = Object.keys(parsedUtmParams).length
                    ? ({ marketingData: parsedUtmParams } as {
                        marketingData: { [key: string]: string };
                      })
                    : {};

                  mixpanel.track(MIXPANEL_EVENT.USER.CLICK_SIGNUP, {
                    type: oauthSignupContext
                      ? oauthSignupContext.providerType.toLowerCase()
                      : 'email',
                  });

                  const response = await signUp({
                    ...values,
                    ...marketingParams,
                    ...(referrerClobeUserId ? { referrerClobeUserId } : {}),
                    oauthConnectionToken: oauthSignupContext?.oauthConnectionToken,
                    ...(partner ? { partner } : {}),
                    initialSignupService: 'FINANCE',
                  });

                  if (response.Status === ResponseStatus.SUCCESS) {
                    window.fbq('track', '[Click] clobe sign-up');
                    await sessionStorage.removeItem('signupSteps');
                    // MEMO: 유저 이메일을 알수 있는구좌가 없음
                    await sessionStorage.setItem('user-email', values.email);
                    // 유저 생성 완료 후 이벤트 전송

                    window.dataLayer = window.dataLayer || [];
                    if (window.dataLayer && window.dataLayer.push) {
                      window.dataLayer.push({
                        event: 'create_user',
                        category: 'User Interaction',
                        action: 'Create',
                        label: 'User Created',
                        email: values.email,
                        phone_number: values.phoneNumber.replace(/^0/, '+82'),
                      });
                    }
                    // 회원가입 완료(C1)
                    trackNaverConversion('sign_up');

                    // 초대 받은 경우 AcquisitionChannel 스킵
                    const companyInvitationSession = getCompanyInvitationSession();
                    if (companyInvitationSession !== undefined) {
                      await router.push(
                        `/accept/company/invitation/auto?token=${companyInvitationSession.token}`,
                      );
                      return;
                    }

                    await router.push('/clobe/home');
                  } else {
                    Toast.error(
                      match(response.ErrorCode)
                        .with('INVALID_RECOMMENDATION_CODE', () => '유효하지 않은 추천 코드입니다')
                        .with('DUPLICATE_LOGIN_INFO', () => '이미 존재하는 계정입니다')
                        .otherwise(() => '회원가입에 실패했습니다'),
                    );
                  }
                }}
              />
            );
          }}
        </Steps.Item>
      </Steps>
    </AnimatePresence>
  );
};

export default SignupSteps;
